import { useQuery } from "@tanstack/react-query";
import { useId } from "react";

import { cn } from "@/app/utils/cn";

import { supportRequestService } from "@/app/services/supportRequestService";

export const SUPPORT_REQUESTS_CHART_QUERY_KEY = "supportRequestsChart";

const WIDTH = 176;
const HEIGHT = 36;
const PAD = 2;

interface SupportRequestsChartProps {
  isActive?: boolean;
  isMuted?: boolean;
}

export const SupportRequestsChart = ({
  isActive,
  isMuted,
}: SupportRequestsChartProps) => {
  const gradientId = useId();

  const { data } = useQuery({
    queryKey: [SUPPORT_REQUESTS_CHART_QUERY_KEY],
    queryFn: supportRequestService.getSupportRequestsChart,
    staleTime: 5 * 60_000,
  });

  if (!data) return null;

  const { days, total } = data;
  const max = Math.max(1, ...days.map((day) => day.count));
  const step = days.length > 1 ? WIDTH / (days.length - 1) : WIDTH;

  const points = days.map((day, index) => ({
    x: days.length > 1 ? index * step : WIDTH,
    y: HEIGHT - PAD - (day.count / max) * (HEIGHT - PAD * 2),
  }));
  if (points.length === 1) points.unshift({ x: 0, y: points[0].y });

  const line = points.map(({ x, y }) => `${x},${y}`).join(" ");
  const area = `0,${HEIGHT} ${line} ${WIDTH},${HEIGHT}`;

  return (
    <div className="flex flex-col gap-1">
      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        className={cn(
          "h-9 w-full overflow-visible",
          isActive
            ? isMuted
              ? "text-white/50"
              : "text-white"
            : isMuted
              ? "text-gray-300"
              : "text-primary",
        )}
        role="img"
        aria-label={`${total} chamados criados nos últimos 7 dias`}
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="currentColor" stopOpacity={0.25} />
            <stop offset="100%" stopColor="currentColor" stopOpacity={0} />
          </linearGradient>
        </defs>

        <polygon points={area} fill={`url(#${gradientId})`} />
        <polyline
          points={line}
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinejoin="round"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
    </div>
  );
};
